import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  applyTheme,
  isValidTheme,
  subscribeToSystemTheme,
} from '../lib/theme';
import { setCookie, getCookie, COOKIE_KEYS } from '../lib/cookies';
import { validatePartyExists } from '../lib/partyValidator';
import { clearRoomHistory } from '../features/nav/roomHistoryStore';
import {
  GLOB,
  partyCodeFromPath,
  pathForPartyCode,
} from '../router/paths';

// localStorage keys. `partyCode` is preserved only as "last known room"
// memory — the URL is the source of truth at runtime.
const KEYS = {
  user: 'user',
  handle: 'userName',
  partyCode: 'partyCode',
  theme: 'theme',
  blocked: 'blocked',
  avatarSalt: 'avatarSalt',
  transportByRoom: 'transportByRoom',
};

const TRANSPORT_VALUES = new Set(['lan', 'cloud']);

// Parse a stored transportByRoom payload back into a sanitized record. The
// shape on disk is `{ <ROOM_CODE>: 'lan' | 'cloud' }`. Bad entries are dropped
// silently — a malformed payload from another tab should not poison the map.
function parseTransportByRoom(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out = {};
    for (const [code, value] of Object.entries(parsed)) {
      if (typeof code === 'string' && code && TRANSPORT_VALUES.has(value)) {
        out[code] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

const DEFAULT_THEME = 'auto';
const COOKIE_TTL_DAYS = 1;

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    /* quota / disabled storage — non-fatal */
  }
}

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `anon-${crypto.randomUUID()}`;
  }
  return `anon-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

// Hydrate the handle from cookie when localStorage is empty.
function readHandleInitial() {
  const local = readStorage(KEYS.handle);
  if (local) return local;
  const cookie = getCookie(COOKIE_KEYS.handle);
  if (cookie) {
    writeStorage(KEYS.handle, cookie);
    return cookie;
  }
  return '';
}

function readBlockedInitial() {
  const raw = readStorage(KEYS.blocked);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

function readAvatarSaltInitial() {
  const raw = readStorage(KEYS.avatarSalt);
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function readTransportByRoomInitial() {
  return parseTransportByRoom(readStorage(KEYS.transportByRoom));
}

export const IdentityContext = createContext(null);

export function IdentityProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(() => readStorage(KEYS.user) || '');
  const [handle, setHandleState] = useState(() => readHandleInitial());
  const [theme, setThemeState] = useState(() => {
    const stored = readStorage(KEYS.theme);
    return isValidTheme(stored) ? stored : DEFAULT_THEME;
  });
  const [blocked, setBlockedState] = useState(() => readBlockedInitial());
  const [avatarSalt, setAvatarSaltState] = useState(() => readAvatarSaltInitial());
  // Per-room Beam transport default ({ <CODE>: 'lan' | 'cloud' }). The map
  // grows lazily — rooms without an explicit pick are simply absent here and
  // useTransport() falls back to 'cloud'.
  const [transportByRoom, setTransportByRoomState] = useState(() =>
    readTransportByRoomInitial(),
  );
  // expiredFrom: one-shot signal consumed by the chat orchestrator to surface
  // "Party XKQP expired — back in #GLOB". Cleared via clearExpired().
  const [expiredFrom, setExpiredFrom] = useState(null);

  // partyCode is URL-derived. The URL is the source of truth for room state —
  // navigation drives partyCode; partyCode is never stored in component state.
  // Malformed /p/:code params resolve to GLOB here so background machinery
  // (presence, message subscriptions) stays sane; the dedicated PartyNotFound
  // route component handles the UX for malformed/missing codes.
  const partyCode = useMemo(() => {
    const parsed = partyCodeFromPath(location.pathname);
    return parsed === null ? GLOB : parsed;
  }, [location.pathname]);

  // Mirror the URL-derived partyCode to localStorage + cookie as "last known
  // room" memory. The `/` route uses this to redirect returning visitors.
  useEffect(() => {
    if (!partyCode) return;
    writeStorage(KEYS.partyCode, partyCode);
    setCookie(COOKIE_KEYS.partyCode, partyCode, COOKIE_TTL_DAYS);
  }, [partyCode]);

  // Persist default theme on first mount if absent.
  useEffect(() => {
    if (readStorage(KEYS.theme) === null) {
      writeStorage(KEYS.theme, DEFAULT_THEME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Establish a STABLE, PER-BROWSER identity on first visit.
  //
  // This used to be the visitor's public IP (fetched from geolocation-db.com).
  // That is fundamentally wrong for a device-to-device feature: two devices
  // behind the same router share ONE public IP (NAT), so a phone + a desktop on
  // the same Wi-Fi got the SAME `user`. Presence is keyed by `user`, so they
  // collided into a single row and each filtered the OTHER out as "self" — the
  // exact reason Beam showed no nearby device and transfers never started.
  //
  // A per-browser id keeps every device distinct. Same-LAN detection does NOT
  // rely on this value — it uses the STUN srflx IP from usePublicIp (the
  // presence `public_ip` field), which is supposed to be shared across the LAN.
  // Dropping the network fetch also removes the geolocation-db.com hang / the
  // "__cf_bm cookie rejected" warning entirely.
  useEffect(() => {
    if (readStorage(KEYS.user)) return;
    const id = generateSessionId();
    writeStorage(KEYS.user, id);
    setUser(id);
    // Mount-only. Synchronous (no async/ref), so StrictMode's double-invoke is
    // safe: the first mount writes storage, the second reads it and returns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Party-existence probe — runs at most once per session, for the FIRST
  // non-GLOB code we resolve from the URL (i.e. the user landed here via
  // IndexRedirect from localStorage, or via a typed/share-linked URL).
  //
  // Crucially, the probe does NOT run on user-initiated navigation
  // (setPartyCode / leaveParty) — those are explicit "enter this party"
  // intents and second-guessing them breaks the Throw flow (a brand-new
  // party has zero messages so the existence check would always fail) and
  // the Join flow (joining an empty room right after a friend Threw it
  // would kick the user back out before they could see anything).
  //
  // Caches probed codes in a Set so a return-visit to the same room
  // doesn't re-probe.
  const probedRef = useRef(new Set());
  // Flipped to true by setPartyCode / leaveParty so the probe knows to
  // skip the next partyCode change.
  const userIntentRef = useRef(false);
  useEffect(() => {
    if (partyCode === GLOB) return undefined;
    if (userIntentRef.current) {
      userIntentRef.current = false;
      probedRef.current.add(partyCode);
      return undefined;
    }
    if (probedRef.current.has(partyCode)) return undefined;
    probedRef.current.add(partyCode);

    let cancelled = false;
    (async () => {
      const exists = await validatePartyExists(partyCode);
      if (cancelled || exists) return;
      const previous = partyCode;
      setExpiredFrom(previous);
      userIntentRef.current = true; // mark the bounce-back as user-ish so
      // the probe doesn't re-fire when partyCode resolves to GLOB
      navigate(pathForPartyCode(GLOB), { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [partyCode, navigate]);

  // Theme application + system listener.
  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'auto') return undefined;
    const unsubscribe = subscribeToSystemTheme(() => applyTheme(theme));
    return unsubscribe;
  }, [theme]);

  // Multi-tab sync via the storage event.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea && e.storageArea !== localStorage) return;
      switch (e.key) {
        case KEYS.user:
          setUser(e.newValue || '');
          break;
        case KEYS.handle:
          setHandleState(e.newValue || '');
          break;
        case KEYS.theme:
          if (isValidTheme(e.newValue)) setThemeState(e.newValue);
          break;
        case KEYS.blocked: {
          if (!e.newValue) {
            setBlockedState([]);
            break;
          }
          try {
            const parsed = JSON.parse(e.newValue);
            if (Array.isArray(parsed)) {
              setBlockedState(
                parsed.filter((id) => typeof id === 'string' && id.length > 0),
              );
            }
          } catch {
            /* corrupt payload from sibling tab — ignore */
          }
          break;
        }
        case KEYS.avatarSalt: {
          const n = Number.parseInt(e.newValue || '0', 10);
          setAvatarSaltState(Number.isFinite(n) && n >= 0 ? n : 0);
          break;
        }
        case KEYS.transportByRoom:
          setTransportByRoomState(parseTransportByRoom(e.newValue));
          break;
        case null:
          // storage.clear() from another tab
          setUser('');
          setHandleState('');
          setThemeState(DEFAULT_THEME);
          setBlockedState([]);
          setAvatarSaltState(0);
          setTransportByRoomState({});
          break;
        default:
          break;
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Actions.
  const setHandle = useCallback((next) => {
    const value = next ?? '';
    writeStorage(KEYS.handle, value);
    setCookie(COOKIE_KEYS.handle, value, COOKIE_TTL_DAYS);
    setHandleState(value);
  }, []);

  // setPartyCode now navigates — URL is truth. localStorage is updated via the
  // URL-watching effect above, so we don't need to write it here.
  // userIntentRef marks this as a deliberate user action so the
  // party-existence probe won't second-guess us (see probe effect above).
  const setPartyCode = useCallback(
    (next) => {
      userIntentRef.current = true;
      navigate(pathForPartyCode(next || GLOB));
    },
    [navigate],
  );

  const leaveParty = useCallback(() => {
    userIntentRef.current = true;
    navigate(pathForPartyCode(GLOB));
  }, [navigate]);

  const setTheme = useCallback((next) => {
    if (!isValidTheme(next)) return;
    writeStorage(KEYS.theme, next);
    setThemeState(next);
  }, []);

  const block = useCallback((userId) => {
    if (typeof userId !== 'string' || !userId) return;
    setBlockedState((prev) => {
      if (prev.includes(userId)) return prev;
      const next = [...prev, userId];
      writeStorage(KEYS.blocked, JSON.stringify(next));
      return next;
    });
  }, []);

  const unblock = useCallback((userId) => {
    if (typeof userId !== 'string' || !userId) return;
    setBlockedState((prev) => {
      if (!prev.includes(userId)) return prev;
      const next = prev.filter((id) => id !== userId);
      writeStorage(KEYS.blocked, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearExpired = useCallback(() => {
    setExpiredFrom(null);
  }, []);

  // Reroll the avatar by bumping a salt counter. Every avatar-rendering
  // surface (UserMiniCard, MobileNav, UserModal, OnboardingScreen,
  // MessageList for self-messages) derives `avatarSeed` from
  // (user|handle) + salt, so a single bump propagates everywhere.
  // Persisted to localStorage so the chosen avatar survives reloads;
  // synced across tabs via the storage event.
  // Per-room Beam transport pick. Validates the value, writes through to
  // localStorage as a JSON-stringified `{ <CODE>: 'lan'|'cloud' }` map, and
  // re-emits a new object so React picks the change up. Bad input is a no-op
  // — callers should never reach here with anything other than 'lan'/'cloud',
  // but defending here keeps the on-disk shape clean.
  const setTransport = useCallback((roomCode, value) => {
    if (typeof roomCode !== 'string' || !roomCode) return;
    if (!TRANSPORT_VALUES.has(value)) return;
    setTransportByRoomState((prev) => {
      if (prev[roomCode] === value) return prev;
      const next = { ...prev, [roomCode]: value };
      writeStorage(KEYS.transportByRoom, JSON.stringify(next));
      return next;
    });
  }, []);

  const rerollAvatar = useCallback(() => {
    setAvatarSaltState((prev) => {
      const next = (prev + 1) % 1_000_000; // bounded; resets every million bumps
      writeStorage(KEYS.avatarSalt, String(next));
      return next;
    });
  }, []);

  const forgetMe = useCallback(() => {
    writeStorage(KEYS.user, null);
    writeStorage(KEYS.handle, null);
    writeStorage(KEYS.partyCode, null);
    writeStorage(KEYS.theme, null);
    writeStorage(KEYS.blocked, null);
    writeStorage(KEYS.avatarSalt, null);
    writeStorage(KEYS.transportByRoom, null);
    // Evict mirrored cookies so a reload doesn't restore identity.
    setCookie(COOKIE_KEYS.handle, '', 0);
    setCookie(COOKIE_KEYS.partyCode, '', 0);
    setUser('');
    setHandleState('');
    setThemeState(DEFAULT_THEME);
    setBlockedState([]);
    setAvatarSaltState(0);
    setTransportByRoomState({});
    setExpiredFrom(null);
    probedRef.current.clear();
    // Drop the cached room-history (in-memory + sessionStorage) so a
    // "forgotten" identity doesn't leave its rooms behind for the next visitor.
    clearRoomHistory();
    navigate('/');
  }, [navigate]);

  // Single source of truth for the avatar seed. Surfaces that show the
  // visitor's avatar (UserMiniCard, MobileNav, UserModal, ProfileDrawer,
  // OnboardingScreen, MessageList for self-messages) must read this so a
  // reroll in one place propagates everywhere.
  const avatarBase = user || handle || 'glacier-anon';
  const avatarSeed = avatarSalt === 0
    ? avatarBase
    : `${avatarBase}-r${avatarSalt}`;

  const value = useMemo(
    () => ({
      user,
      handle,
      partyCode,
      theme,
      blocked,
      avatarSalt,
      avatarSeed,
      transportByRoom,
      expiredFrom,
      hasHandle: !!handle,
      isInGlob: partyCode === GLOB,
      setHandle,
      setPartyCode,
      leaveParty,
      setTheme,
      block,
      unblock,
      rerollAvatar,
      setTransport,
      clearExpired,
      forgetMe,
    }),
    [
      user,
      handle,
      partyCode,
      theme,
      blocked,
      avatarSalt,
      avatarSeed,
      transportByRoom,
      expiredFrom,
      setHandle,
      setPartyCode,
      leaveParty,
      setTheme,
      block,
      unblock,
      rerollAvatar,
      setTransport,
      clearExpired,
      forgetMe,
    ],
  );

  return (
    <IdentityContext.Provider value={value}>
      {children}
    </IdentityContext.Provider>
  );
}

export default IdentityProvider;
